// Captures the ad click ids and Meta's browser ids onto the cart, so the order
// carries them to the server-side purchase events (disctree-rust#943).
//
// Only with marketing consent (Shopify's Customer Privacy API): without it
// nothing is read, created or written, and ids already on the cart are cleared
// when consent is withdrawn.
//
// Written onto the cart as underscore-prefixed attributes, which checkout hides
// from the customer and Shopify copies onto the order:
//   _dt_marketing_consent     "granted" or "denied"
//   _dt_fbp, _dt_fbc          Meta's browser id and click id cookies
//   _dt_gclid, _dt_gbraid, _dt_wbraid
//                             Google's click ids, the newest click only
//   _dt_storefront_domain     the domain the order is placed on (disctree.se),
//                             which picks the market's Ads account; the order
//                             itself only carries the primary domain
//   _dt_ga_client_id, _dt_ga_session_id
//                             the visitor's GA4 client and session id, so the
//                             server's copy of the purchase joins the visit and
//                             GA4 counts it once with the pixel's; only with
//                             analytics consent too, once gtag has set its cookies
// The server reads these names; change both together.
//
// Meta's `_fbp` and `_fbc` cookies are created here, not by Meta: Meta's own
// script does not set them when it runs inside Shopify's pixel sandbox, which
// is where our Meta custom pixel runs (checked 2026-09-29 on disctree.dk with
// marketing consent and an `fbclid` in the URL: `_ga`, `_gcl_au`, `_ttp` and
// `_pin_unauth` were set, `_fbp` and `_fbc` were not). The formats are Meta's:
// https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
//   _fbp = fb.<subdomainIndex>.<creationTime ms>.<random number>
//   _fbc = fb.<subdomainIndex>.<creationTime ms>.<fbclid>
// with subdomainIndex 1 for a cookie set on the registrable domain
// (disctree.dk), and a 90-day lifetime like Meta's own.

(() => {
  const COOKIE_DAYS = 90;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const GOOGLE_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'];

  // The cart's attributes as rendered into the page by the layout.
  const current = window.dtCartAttributes || {};

  function registrableDomain() {
    return location.hostname.split('.').slice(-2).join('.');
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  // Lives COOKIE_DAYS from now, or until `expiresAt` (ms) when given.
  function writeCookie(name, value, expiresAt = Date.now() + COOKIE_DAYS * DAY_MS) {
    const expires = new Date(expiresAt).toUTCString();
    document.cookie =
      name + '=' + encodeURIComponent(value) + '; expires=' + expires +
      '; path=/; domain=.' + registrableDomain() + '; SameSite=Lax; Secure';
  }

  function deleteCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.' + registrableDomain();
  }

  // Meta's browser id: kept if present, created otherwise, and rewritten
  // either way so its lifetime restarts on each visit.
  function ensureFbp() {
    const fbp = readCookie('_fbp') || 'fb.1.' + Date.now() + '.' + Math.floor(Math.random() * 1e10);
    writeCookie('_fbp', fbp);
    return fbp;
  }

  // Meta's click id: a new `fbclid` in the URL replaces the stored one. It lives
  // COOKIE_DAYS from the click, not from the latest visit: Meta treats an older
  // click as expired, so one past that (or unreadable) is dropped.
  function ensureFbc(params) {
    const fbclid = params.get('fbclid');
    let fbc = readCookie('_fbc');
    if (fbclid && (!fbc || !fbc.endsWith('.' + fbclid))) {
      fbc = 'fb.1.' + Date.now() + '.' + fbclid;
    }
    if (!fbc) return null;
    const expiresAt = Number(fbc.split('.')[2]) + COOKIE_DAYS * DAY_MS;
    if (!(expiresAt > Date.now())) {
      deleteCookie('_fbc');
      return null;
    }
    writeCookie('_fbc', fbc, expiresAt);
    return fbc;
  }

  // GA4's ids, read from the cookies gtag sets; empty until it has set them.
  // `_ga` is `GA1.1.<client id>`; the stream's `_ga_<id>` cookie carries the
  // session id, read only while it is the one GA4 stream cookie.
  function ga4Ids() {
    const client = readCookie('_ga')?.match(/^GA\d\.\d+\.(\d+\.\d+)$/);
    const streams = document.cookie.match(/(?:^|; )_ga_[A-Z0-9]+=[^;]*/g) || [];
    const stream = streams.length === 1 ? streams[0].split('=')[1] : '';
    const session = stream.match(/^GS1\.\d+\.(\d+)\./) || stream.match(/^GS2\.\d+\.s(\d+)/);
    return {
      _dt_ga_client_id: client ? client[1] : '',
      _dt_ga_session_id: client && session ? session[1] : '',
    };
  }

  function granted(analytics) {
    const params = new URLSearchParams(location.search);
    const attributes = {
      _dt_marketing_consent: 'granted',
      _dt_fbp: ensureFbp(),
      _dt_fbc: ensureFbc(params) || '',
      _dt_storefront_domain: registrableDomain(),
      ...(analytics ? ga4Ids() : { _dt_ga_client_id: '', _dt_ga_session_id: '' }),
    };
    const googleParam = GOOGLE_CLICK_PARAMS.find((name) => params.get(name));
    if (googleParam) {
      // A new Google click replaces the old one of any kind.
      for (const name of GOOGLE_CLICK_PARAMS) {
        attributes['_dt_' + name] = name === googleParam ? params.get(name) : '';
      }
    }
    return attributes;
  }

  function denied() {
    deleteCookie('_fbp');
    deleteCookie('_fbc');
    const attributes = {
      _dt_marketing_consent: 'denied',
      _dt_fbp: '',
      _dt_fbc: '',
      _dt_storefront_domain: '',
      _dt_ga_client_id: '',
      _dt_ga_session_id: '',
    };
    for (const name of GOOGLE_CLICK_PARAMS) attributes['_dt_' + name] = '';
    return attributes;
  }

  // Writes only what differs from the cart, so most page loads send nothing.
  function update(attributes) {
    const changed = Object.entries(attributes).filter(([key, value]) => (current[key] || '') !== value);
    if (changed.length === 0) return;
    const body = Object.fromEntries(changed);
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: body }),
    }).then((response) => {
      if (response.ok) Object.assign(current, body);
    });
  }

  function apply() {
    const privacy = window.Shopify.customerPrivacy;
    if (privacy.marketingAllowed()) {
      update(granted(privacy.analyticsProcessingAllowed()));
    } else if (current._dt_marketing_consent === 'granted') {
      // Consent was withdrawn since the ids were captured.
      update(denied());
    }
  }

  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) return;
    apply();
    // The visitor answering the cookie banner during this page view.
    document.addEventListener('visitorConsentCollected', apply);
    // gtag sets its cookies after this script has run on a first page view,
    // and starts a new session after 30 idle minutes; a cart change picks
    // both up.
    subscribe(PUB_SUB_EVENTS.cartUpdate, apply);
  });
})();
